import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./Dialog.css";

export function useDialog() {
  const [state, setState] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "OK",
    cancelText: "Cancel",
    showCancel: true,
    dangerous: false,
    requireInput: null,
  });

  const show = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        title: "",
        message: "",
        confirmText: "OK",
        cancelText: "Cancel",
        showCancel: true,
        dangerous: false,
        requireInput: null,
        ...opts,
        onConfirm: () => {
          setState((s) => ({ ...s, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setState((s) => ({ ...s, isOpen: false }));
          resolve(false);
        },
      });
    });
  }, []);

  const confirm = useCallback(
    (message, opts = {}) =>
      show({ message, confirmText: "Confirm", showCancel: true, dangerous: false, ...opts }),
    [show]
  );

  const alert = useCallback(
    (message, opts = {}) =>
      show({ message, confirmText: "OK", showCancel: false, dangerous: false, ...opts }),
    [show]
  );

  return { dialogState: state, confirm, alert };
}

export function Dialog({ dialogState }) {
  const [inputVal, setInputVal] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (dialogState.isOpen) {
      setInputVal("");
      if (dialogState.requireInput) {
        setTimeout(() => inputRef.current?.focus(), 60);
      }
    }
  }, [dialogState.isOpen, dialogState.requireInput]);

  useEffect(() => {
    if (dialogState.isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [dialogState.isOpen]);

  if (!dialogState.isOpen) return null;

  const canConfirm = !dialogState.requireInput || inputVal === dialogState.requireInput;

  return createPortal(
    <div
      className="dialog-overlay"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (dialogState.showCancel) dialogState.onCancel?.();
        else dialogState.onConfirm?.();
      }}
    >
      <div className="dialog-box">
        {dialogState.title && <h3 className="dialog-title">{dialogState.title}</h3>}
        <p className="dialog-message">{dialogState.message}</p>

        {dialogState.requireInput && (
          <div className="dialog-input-group">
            <label className="dialog-input-label">
              Type <span className="dialog-input-hint">"{dialogState.requireInput}"</span> to confirm:
            </label>
            <input
              ref={inputRef}
              className="dialog-input"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canConfirm) dialogState.onConfirm?.();
                if (e.key === "Escape") dialogState.onCancel?.();
              }}
              autoComplete="off"
            />
          </div>
        )}

        <div className="dialog-actions">
          {dialogState.showCancel && (
            <button onClick={dialogState.onCancel} className="dialog-btn dialog-btn-ghost">
              {dialogState.cancelText}
            </button>
          )}
          <button
            onClick={dialogState.onConfirm}
            disabled={!canConfirm}
            className={`dialog-btn ${dialogState.dangerous ? "dialog-btn-danger" : "dialog-btn-primary"}`}
          >
            {dialogState.confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
